import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { formatDate } from "../utils/formatDate";
import {
  FileJson,
  ChevronLeft,
  ChevronRight,
  Upload,
  Zap,
  Eye,
  Trash2,
  RefreshCw,
  X,
  File,
  CheckCircle,
  AlertCircle,
  Clock,
} from "lucide-react";
import { v1GetWorkOrders, v1GetStats, v1Extract, v1DeleteWorkOrder } from "../api";
import "./V1WorkOrdersList.css";

// Model type display config
const MODEL_CONFIG = {
  xorbit: {
    name: "XOrbit",
    description: "Gemini 3.5 Flash",
    color: "#8b5cf6",
    bgColor: "#f3e8ff",
  },
  vja: {
    name: "VJA",
    description: "Qwen VL",
    color: "#0ea5e9",
    bgColor: "#e0f2fe",
  },
};

// Custom Dropdown Component for Status Filter
const CustomStatusDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const options = [
    { value: "all", label: "All Status" },
    { value: "pending", label: "Pending" },
    { value: "processing", label: "Processing" },
    { value: "completed", label: "Completed" },
    { value: "failed", label: "Failed" },
  ];

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div className="v1-custom-dropdown" ref={dropdownRef}>
      <button className="v1-dropdown-trigger" onClick={() => setIsOpen(!isOpen)}>
        <span>{selectedOption?.label}</span>
        <svg className={`dropdown-arrow ${isOpen ? "open" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>
      {isOpen && (
        <div className="v1-dropdown-menu">
          {options.map((option) => (
            <button
              key={option.value}
              className={`v1-dropdown-option ${value === option.value ? "selected" : ""}`}
              onClick={() => { onChange(option.value); setIsOpen(false); }}
            >
              {option.label}
              {value === option.value && <span className="check-mark">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Upload Modal Component
const UploadModal = ({ isOpen, onClose, modelType, modelConfig, onUploadComplete }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({}); // { filename: 'pending' | 'uploading' | 'success' | 'error' }
  const [uploadResults, setUploadResults] = useState([]);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    addFiles(selectedFiles);
  };

  const addFiles = (newFiles) => {
    const pdfFiles = newFiles.filter(f => f.name.toLowerCase().endsWith('.pdf'));
    const existingNames = files.map(f => f.name);
    const uniqueFiles = pdfFiles.filter(f => !existingNames.includes(f.name));
    
    if (pdfFiles.length !== newFiles.length) {
      alert('Only PDF files are supported. Non-PDF files were skipped.');
    }
    
    setFiles(prev => [...prev, ...uniqueFiles]);
    
    // Initialize status for new files
    const newStatus = {};
    uniqueFiles.forEach(f => {
      newStatus[f.name] = 'pending';
    });
    setUploadStatus(prev => ({ ...prev, ...newStatus }));
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dropZoneRef.current?.classList.remove('drag-over');
    
    const droppedFiles = Array.from(event.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dropZoneRef.current?.classList.add('drag-over');
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dropZoneRef.current?.classList.remove('drag-over');
  };

  const removeFile = (filename) => {
    setFiles(prev => prev.filter(f => f.name !== filename));
    setUploadStatus(prev => {
      const newStatus = { ...prev };
      delete newStatus[filename];
      return newStatus;
    });
  };

  const handleUploadAll = async () => {
    if (files.length === 0) return;
    
    setUploading(true);
    const results = [];

    for (const file of files) {
      setUploadStatus(prev => ({ ...prev, [file.name]: 'uploading' }));
      
      try {
        const result = await v1Extract(file, modelType);
        setUploadStatus(prev => ({ ...prev, [file.name]: 'success' }));
        results.push({ filename: file.name, success: true, work_order_id: result.work_order_id });
      } catch (err) {
        setUploadStatus(prev => ({ ...prev, [file.name]: 'error' }));
        results.push({ filename: file.name, success: false, error: err.message });
      }
    }

    setUploadResults(results);
    setUploading(false);
    
    // Notify parent to refresh
    if (onUploadComplete) {
      onUploadComplete(results);
    }
  };

  const handleClose = () => {
    setFiles([]);
    setUploadStatus({});
    setUploadResults([]);
    onClose();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'uploading':
        return <RefreshCw size={16} className="spin" style={{ color: '#3b82f6' }} />;
      case 'success':
        return <CheckCircle size={16} style={{ color: '#22c55e' }} />;
      case 'error':
        return <AlertCircle size={16} style={{ color: '#ef4444' }} />;
      default:
        return <Clock size={16} style={{ color: '#9ca3af' }} />;
    }
  };

  if (!isOpen) return null;

  const allUploaded = files.length > 0 && files.every(f => uploadStatus[f.name] === 'success' || uploadStatus[f.name] === 'error');
  const successCount = Object.values(uploadStatus).filter(s => s === 'success').length;
  const errorCount = Object.values(uploadStatus).filter(s => s === 'error').length;

  return (
    <div className="v1-modal-overlay" onClick={handleClose}>
      <div className="v1-upload-modal" onClick={e => e.stopPropagation()}>
        <div className="v1-modal-header" style={{ borderBottomColor: modelConfig.color }}>
          <h3>
            <Upload size={20} style={{ color: modelConfig.color }} />
            Upload Files - {modelConfig.name}
          </h3>
          <button className="v1-modal-close" onClick={handleClose}>
            <X size={20} />
          </button>
        </div>

        <div className="v1-modal-body">
          {/* Drop Zone */}
          <div
            ref={dropZoneRef}
            className="v1-drop-zone"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload size={48} style={{ color: modelConfig.color }} />
            <p>Drag & drop PDF files here</p>
            <span>or click to browse</span>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="v1-file-list">
              <div className="v1-file-list-header">
                <span>{files.length} file(s) selected</span>
                {allUploaded && (
                  <span className="v1-upload-summary">
                    {successCount} success, {errorCount} failed
                  </span>
                )}
              </div>
              <div className="v1-file-items">
                {files.map(file => (
                  <div key={file.name} className={`v1-file-item ${uploadStatus[file.name]}`}>
                    <div className="v1-file-info">
                      <File size={16} />
                      <span className="v1-file-name" title={file.name}>
                        {file.name.length > 40 ? file.name.substring(0, 40) + '...' : file.name}
                      </span>
                      <span className="v1-file-size">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>
                    {uploadStatus[file.name] === 'error' && uploadResults.find(r => r.filename === file.name)?.error && (
                      <div className="v1-file-error" style={{ color: '#ef4444', fontSize: 12, padding: '4px 0 0 24px', wordBreak: 'break-word' }}>
                        {uploadResults.find(r => r.filename === file.name)?.error}
                      </div>
                    )}
                    <div className="v1-file-actions">
                      {getStatusIcon(uploadStatus[file.name])}
                      {uploadStatus[file.name] === 'pending' && !uploading && (
                        <button 
                          className="v1-file-remove"
                          onClick={() => removeFile(file.name)}
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="v1-modal-footer">
          <button className="v1-btn-cancel" onClick={handleClose}>
            {allUploaded ? 'Close' : 'Cancel'}
          </button>
          {!allUploaded && (
            <button
              className="v1-btn-upload-all"
              style={{ backgroundColor: modelConfig.color }}
              onClick={handleUploadAll}
              disabled={files.length === 0 || uploading}
            >
              {uploading ? (
                <>
                  <RefreshCw size={16} className="spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  Upload {files.length} File{files.length !== 1 ? 's' : ''}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const V1WorkOrdersList = ({ modelType = "all" }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [workOrders, setWorkOrders] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0, page: 1, pages: 1, limit: 10, skip: 0,
  });
  const [stats, setStats] = useState({
    total: 0, completed: 0, processing: 0, pending: 0, failed: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [error, setError] = useState("");
  const [showUploadModal, setShowUploadModal] = useState(false);
  
  // Debounce timer ref for search
  const searchTimerRef = useRef(null);

  // Determine active model from route
  const getActiveModel = () => {
    if (location.pathname.includes("/v1/xorbit")) return "xorbit";
    if (location.pathname.includes("/v1/vja")) return "vja";
    return modelType;
  };

  const activeModel = getActiveModel();
  const modelConfig = MODEL_CONFIG[activeModel] || { name: "All Models", color: "#6b7280" };

  useEffect(() => {
    fetchData();
  }, [activeModel, statusFilter]);

  const fetchData = async (skip = 0, search = searchTerm) => {
    try {
      setLoading(true);
      setError("");

      const [ordersRes, statsRes] = await Promise.all([
        v1GetWorkOrders({
          skip,
          limit: 10,
          status: statusFilter !== "all" ? statusFilter : undefined,
          model_type: activeModel !== "all" ? activeModel : undefined,
          search: search || undefined,
        }),
        v1GetStats(activeModel),  // Pass model type to get filtered stats
      ]);

      if (ordersRes.data) {
        setWorkOrders(ordersRes.data);
        setPagination(ordersRes.pagination || { total: ordersRes.data.length, page: 1, pages: 1, limit: 10, skip: 0 });
      }

      // Stats are already filtered by model from backend
      if (statsRes) {
        setStats({
          total: statsRes.total_work_orders || 0,
          completed: statsRes.by_status?.completed || 0,
          processing: statsRes.by_status?.processing || 0,
          pending: statsRes.by_status?.pending || 0,
          failed: statsRes.by_status?.failed || 0,
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle search with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Clear existing timer
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    
    // Debounce search - wait 500ms after user stops typing
    searchTimerRef.current = setTimeout(() => {
      fetchData(0, value);
    }, 500);
  };

  const handleUploadComplete = (results) => {
    // Refresh data after uploads complete
    fetchData(0);
    
    // If only one file was uploaded successfully, navigate to it
    const successResults = results.filter(r => r.success);
    if (successResults.length === 1 && successResults[0].work_order_id) {
      setShowUploadModal(false);
      navigate(`/v1/work-order/${successResults[0].work_order_id}`);
    }
  };

  const handleDelete = async (workOrderId, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this work order?")) return;

    try {
      await v1DeleteWorkOrder(workOrderId);
      fetchData(pagination.skip);
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
    }
  };

  const handleNextPage = () => {
    if (pagination.page < pagination.pages) {
      fetchData(pagination.skip + pagination.limit);
    }
  };

  const handlePrevPage = () => {
    if (pagination.page > 1) {
      fetchData(Math.max(0, pagination.skip - pagination.limit));
    }
  };

  // No need for local filtering anymore - backend handles search
  const filteredWorkOrders = workOrders;

  const getStatusBadgeClass = (status) => {
    const classes = {
      completed: "status-completed",
      processing: "status-processing",
      pending: "status-pending",
      failed: "status-failed",
    };
    return classes[status] || "status-pending";
  };

  return (
    <div className="v1-work-orders-list">
      {/* Header with Model Info */}
      <div className="v1-header-banner" style={{ borderLeftColor: modelConfig.color }}>
        <div className="v1-header-info">
          <h2 style={{ color: modelConfig.color }}>
            <Zap size={24} />
            {modelConfig.name} Work Orders
          </h2>
          <p>{modelConfig.description} - V1 Extraction Pipeline</p>
        </div>
        <div className="v1-header-actions">
          <button
            className="v1-btn-upload"
            onClick={() => setShowUploadModal(true)}
            disabled={activeModel === "all"}
            style={{ backgroundColor: modelConfig.color }}
          >
            <Upload size={18} /> Upload & Extract
          </button>
        </div>
      </div>

      {error && (
        <div className="v1-error-banner">
          {error}
          <button onClick={() => setError("")}>×</button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="v1-stats-grid">
        <div className="v1-stat-card">
          <div className="v1-stat-value">{stats.total}</div>
          <div className="v1-stat-label">Total</div>
        </div>
        <div className="v1-stat-card completed">
          <div className="v1-stat-value">{stats.completed}</div>
          <div className="v1-stat-label">Completed</div>
        </div>
        <div className="v1-stat-card processing">
          <div className="v1-stat-value">{stats.processing}</div>
          <div className="v1-stat-label">Processing</div>
        </div>
        <div className="v1-stat-card pending">
          <div className="v1-stat-value">{stats.pending}</div>
          <div className="v1-stat-label">Pending</div>
        </div>
        <div className="v1-stat-card failed">
          <div className="v1-stat-value">{stats.failed}</div>
          <div className="v1-stat-label">Failed</div>
        </div>
      </div>

      {/* Filters */}
      <div className="v1-filters-row">
        <input
          type="text"
          placeholder="Search by ID, filename, or trace ID..."
          className="v1-search-input"
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <CustomStatusDropdown value={statusFilter} onChange={setStatusFilter} />
        <button className="v1-btn-refresh" onClick={() => fetchData(0)}>
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {/* Table */}
      <div className="v1-table-container">
        <table className="v1-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Filename</th>
              <th>Model</th>
              <th>Status</th>
              <th>Pages</th>
              <th>Created</th>
              <th>Duration</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="v1-table-loading">
                  <RefreshCw size={20} className="spin" /> Loading...
                </td>
              </tr>
            ) : filteredWorkOrders.length === 0 ? (
              <tr>
                <td colSpan="8" className="v1-table-empty">
                  No work orders found. Upload a PDF to get started.
                </td>
              </tr>
            ) : (
              filteredWorkOrders.map((wo) => (
                <tr key={wo._id} onClick={() => navigate(`/v1/work-order/${wo._id}`)}>
                  <td className="v1-id-cell">{wo._id?.slice(-8)}</td>
                  <td className="v1-filename-cell" title={wo.filename}>
                    {wo.filename?.substring(0, 30) || "-"}
                    {wo.filename?.length > 30 && "..."}
                  </td>
                  <td>
                    <span
                      className="v1-model-badge"
                      style={{
                        backgroundColor: MODEL_CONFIG[wo.model_type]?.bgColor || "#f3f4f6",
                        color: MODEL_CONFIG[wo.model_type]?.color || "#6b7280",
                      }}
                    >
                      {MODEL_CONFIG[wo.model_type]?.name || wo.model_type}
                    </span>
                  </td>
                  <td>
                    <span className={`v1-status-badge ${getStatusBadgeClass(wo.status)}`}>
                      {wo.status}
                    </span>
                  </td>
                  <td>{wo.total_pages || "-"}</td>
                  <td>{formatDate(wo.created_at)}</td>
                  <td>{wo.processing_time ? `${wo.processing_time.toFixed(1)}s` : "-"}</td>
                  <td className="v1-actions-cell" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="v1-action-btn view"
                      title="View Details"
                      onClick={() => navigate(`/v1/work-order/${wo._id}`)}
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      className="v1-action-btn json"
                      title="Download JSON"
                      onClick={(e) => {
                        e.stopPropagation();
                        const data = wo.extracted_data || {};
                        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `${wo.filename?.replace(".pdf", "") || wo._id}.json`;
                        a.click();
                      }}
                    >
                      <FileJson size={16} />
                    </button>
                    <button
                      className="v1-action-btn delete"
                      title="Delete"
                      onClick={(e) => handleDelete(wo._id, e)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="v1-pagination">
        <div className="v1-pagination-info">
          Showing {workOrders.length > 0 ? pagination.skip + 1 : 0} - {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
        </div>
        <div className="v1-pagination-controls">
          <button onClick={handlePrevPage} disabled={pagination.page <= 1}>
            <ChevronLeft size={16} /> Previous
          </button>
          <span className="v1-page-indicator">
            Page {pagination.page} of {pagination.pages || 1}
          </span>
          <button onClick={handleNextPage} disabled={pagination.page >= pagination.pages}>
            Next <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Upload Modal */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        modelType={activeModel}
        modelConfig={modelConfig}
        onUploadComplete={handleUploadComplete}
      />
    </div>
  );
};

export default V1WorkOrdersList;
